import { Controller } from "@hotwired/stimulus"

// 記事設定フォームを制御するStimulusコントローラー
export default class extends Controller {
  static targets = [
    "postTimeForm",
    "postTypeSelect",
    "imagePostTypeSelect",
    "imageStyleRequiredMark",
    "imageStyleSelect",
    "imageStyleError",
    "insertImageToContentFormGroup",
    "insertImageToContentCheckbox",
    "insertTitleToImageFormGroup",
    "insertTitleToImageCheckbox",
    "mainKeywordInput"
  ]

  static values = {
    // バズワード収集履歴に含まれる過去のメインキーワード一覧
    pastMainKeywords: { type: Array, default: [] },
    // 他スケジュールが現在設定しているメインキーワード => 重複相手の表示ラベル一覧
    otherMainKeywords: { type: Object, default: {} },
    // 編集前（保存済み）のメインキーワード
    originalMainKeyword: { type: String, default: "" }
  }

  connect() {
    // 初期状態で投稿時刻フォームの表示を制御
    this.togglePostTimeForm()
    // 初期状態で画像スタイルフォームの表示を制御
    // （toggleImageStyleForm()内でtoggleImageOptionForms()も呼ばれる）
    this.toggleImageStyleForm()
  }

  togglePostTimeForm() {
    if (!this.hasPostTimeFormTarget || !this.hasPostTypeSelectTarget) return

    // 予約投稿（scheduled）が選択された場合のみ表示
    if (this.postTypeSelectTarget.value === "scheduled") {
      this.postTimeFormTarget.classList.remove("d-none")
    } else {
      this.postTimeFormTarget.classList.add("d-none")
    }
  }

  toggleImageStyleForm() {
    if (!this.hasImagePostTypeSelectTarget || !this.hasImageStyleSelectTarget) return

    const isImageNone = this.imagePostTypeSelectTarget.value === "none"

    if (isImageNone) {
      // 画像を生成しない: セレクトボックスを非活性にし、「指定なし」を選択状態にする
      this.imageStyleRequiredMarkTarget?.classList.add("d-none")
      this.imageStyleSelectTarget.disabled = true
      this.imageStyleSelectTarget.value = "none"
      this.imageStyleErrorTarget?.classList.add("d-none")
    } else {
      // 画像を生成する: セレクトボックスを活性にし、画像スタイル選択必須
      this.imageStyleRequiredMarkTarget?.classList.remove("d-none")
      this.imageStyleSelectTarget.disabled = false
      // 現在の値が「指定なし」の場合は空にする（ユーザーに選択を促す）
      if (this.imageStyleSelectTarget.value === "none") {
        this.imageStyleSelectTarget.value = ""
      }
      this.imageStyleErrorTarget?.classList.remove("d-none")
    }

    // 画像のオプション（コンテンツ画像挿入・画像内タイトル）フォームの表示も更新
    this.toggleImageOptionForms()
  }

  // 画像を生成する場合のみ有効な画像オプションのフォームを制御する
  toggleImageOptionForms() {
    if (!this.hasImagePostTypeSelectTarget) return

    const isImageNone = this.imagePostTypeSelectTarget.value === "none"

    if (this.hasInsertImageToContentFormGroupTarget) {
      this.toggleImageOptionForm(
        this.insertImageToContentFormGroupTarget,
        this.hasInsertImageToContentCheckboxTarget ? this.insertImageToContentCheckboxTarget : null,
        isImageNone
      )
    }
    if (this.hasInsertTitleToImageFormGroupTarget) {
      this.toggleImageOptionForm(
        this.insertTitleToImageFormGroupTarget,
        this.hasInsertTitleToImageCheckboxTarget ? this.insertTitleToImageCheckboxTarget : null,
        isImageNone
      )
    }
  }

  toggleImageOptionForm(formGroup, checkbox, isImageNone) {
    if (isImageNone) {
      // 画像を生成しない場合は非表示 + チェックボックスをunchecked/disabledにする
      formGroup.classList.add("d-none")
      if (checkbox) {
        checkbox.checked = false
        checkbox.disabled = true
      }
    } else {
      // 画像を生成する場合は表示 + チェックボックスをenabledにする
      formGroup.classList.remove("d-none")
      if (checkbox) {
        checkbox.disabled = false
      }
    }
  }

  // メインキーワードが過去のバズワード収集や他スケジュールと重複する場合、送信前に一度だけ確認する
  // （過去に記事採用し尽くしたキーワードの再設定や、他スケジュールとの記事かぶりを防ぐ）
  confirmMainKeyword(event) {
    if (!this.hasMainKeywordInputTarget) return

    const value = this.mainKeywordInputTarget.value.trim()
    if (!value) return
    // 変更していない場合は通常の編集フローなので確認しない
    if (value === this.originalMainKeywordValue) return

    const reasons = []
    if (this.pastMainKeywordsValue.includes(value)) {
      reasons.push("過去に設定したキーワードです。")
    }
    // hasOwnProperty: "constructor" 等のプロトタイプ由来のキーを誤検知しないため
    // （Object.hasOwn は Safari 15.4 未満が未対応で、Vite もポリフィルを入れないため使わない）
    if (Object.prototype.hasOwnProperty.call(this.otherMainKeywordsValue, value)) {
      const labels = this.otherMainKeywordsValue[value]
      reasons.push(`他のスケジュール（${this.formatScheduleLabels(labels)}）で使用中のキーワードです。`)
    }
    if (reasons.length === 0) return

    if (!window.confirm(`${reasons.join("\n")}\n設定してよろしいですか？`)) {
      event.preventDefault()
    }
  }

  // 重複相手が複数の場合は文言が長くなるため、先頭1件のみ表示して残りは件数で示す
  formatScheduleLabels(labels) {
    const restCount = labels.length - 1
    return restCount > 0 ? `${labels[0]}（他${restCount}件）` : labels[0]
  }
}
